import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Modal, Select, Table, Tooltip, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  type UndeliverableUploadPreviewResult,
  type UndeliverableUploadOrderFilterVariables,
  type UndeliverableUploadTaskKeyInput,
  useUndeliverableUploadMutations,
  useUndeliverableUploadQueries,
} from './hooks/useUndeliverableUpload';
import AsyncReportDownloadModal, {
  dashboardAsyncReportLabelI18nKeys,
} from '../../../components/asyncReportDownload';
import { formatISODateTime } from '../../../utils/dateTimeUtils';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import UndeliverableUploadTaskDrawer, {
  type UndeliverableUploadTaskRow as DrawerTaskRow,
} from './components/UndeliverableUploadTaskDrawer';
import InputFilterItem from '../../delivery/components/InputFilterItem';
import DayjsRangePickerFilterItem from '../../delivery/components/DayjsRangePickerFilterItem';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../utils/labelWidthUtils';

const MAX_FILE_SIZE = 5 * 1024 * 1024;

/** Matches the console info alert style: 1px border with a light purple background. */
const INSTRUCTION_PURPLE_BOX_CLASS =
  'mb-4 rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 text-black';

const UNDELIVERABLE_TEMPLATE_PUBLIC_PATH = '/QC-无法妥投明细导入模版.xlsx';

const readFileAsBase64 = (file: File, failText: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(failText));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error(failText));
        return;
      }
      const idx = result.indexOf('base64,');
      resolve(idx >= 0 ? result.slice(idx + 'base64,'.length) : result);
    };
    reader.readAsDataURL(file);
  });

type UploadTaskRow = DrawerTaskRow & {
  uploadTime: string | null;
  uploaderDisplay: string;
};

type UploadTaskFilters = {
  fileName: string;
  uploaderAccountId: number | null;
  uploadTimeRange: [Dayjs, Dayjs] | null;
};

const getDefaultUploadTimeRange = (): [Dayjs, Dayjs] => {
  const end = dayjs();
  return [end.subtract(7, 'day'), end];
};

const createDefaultFilters = (): UploadTaskFilters => ({
  fileName: '',
  uploaderAccountId: null,
  uploadTimeRange: getDefaultUploadTimeRange(),
});

const toQueryVariables = (filters: UploadTaskFilters): UndeliverableUploadOrderFilterVariables => {
  const fileName = filters.fileName.trim();
  const range = filters.uploadTimeRange;
  return {
    fileName: fileName || null,
    uploaderAccountId: filters.uploaderAccountId,
    uploadTime: range ? { start: range[0].startOf('day').toISOString(), end: range[1].endOf('day').toISOString() } : null,
  };
};

type UndeliverableTaskSummaryFromApi = {
  taskNo: string;
  fileObjectKey: string;
  originalFileName?: string | null;
  createdBy?: string | null;
  uploaderName?: string | null;
  uploadTime?: string | null;
  rowCount: number;
  legacyGroup: boolean;
};

const displayFileNameFromTask = (originalFileName: string | null | undefined, fileObjectKey: string): string => {
  const n = originalFileName?.trim();
  if (n) return n;
  const k = fileObjectKey || '';
  const i = k.lastIndexOf('/');
  return i >= 0 ? k.slice(i + 1) : k;
};

const normalizePreviewFromGraphql = (preview: {
  totalCount?: number;
  validCount?: number;
  invalidCount?: number;
  validPayloadFingerprint?: string;
  errors?: { rowNo?: number; message?: string }[];
} | null | undefined): UndeliverableUploadPreviewResult | null => {
  if (!preview) return null;
  return {
    totalCount: Number(preview.totalCount ?? 0),
    validCount: Number(preview.validCount ?? 0),
    invalidCount: Number(preview.invalidCount ?? 0),
    validPayloadFingerprint: String(preview.validPayloadFingerprint ?? '').trim(),
    errors: (preview.errors ?? []).map((e) => ({
      rowNo: Number(e.rowNo),
      message: String(e.message ?? ''),
    })),
  };
};

type GraphQlFormattedLike = { message?: string; extensions?: Record<string, unknown> };

/** 确认上传失败：优先用 GraphQL extensions.messageKey + details 走 i18n，与后端 BusinessException 对齐 */
const userMessageFromUndeliverableConfirmGraphqlError = (err: unknown, t: TFunction, fallbackKey: string): string => {
  const gqlErrors =
    err &&
    typeof err === 'object' &&
    'graphQLErrors' in err &&
    Array.isArray((err as { graphQLErrors: GraphQlFormattedLike[] }).graphQLErrors)
      ? (err as { graphQLErrors: GraphQlFormattedLike[] }).graphQLErrors
      : undefined;
  const first = gqlErrors?.[0];
  const mk =
    first?.extensions && typeof first.extensions.messageKey === 'string' ? first.extensions.messageKey : '';
  if (mk && first) {
    const rawDetails = first.extensions?.details;
    const details =
      rawDetails && typeof rawDetails === 'object' && rawDetails !== null && !Array.isArray(rawDetails)
        ? (rawDetails as Record<string, string | number | boolean>)
        : {};
    return String(t(mk, { defaultValue: first.message ?? '', ...details }));
  }
  const msg =
    (typeof first?.message === 'string' && first.message) || (err instanceof Error ? err.message : '') || '';
  return msg || String(t(fallbackKey));
};

const UndeliverableUploadPage: React.FC = () => {
  const { t } = useTranslation();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewResult, setPreviewResult] = useState<UndeliverableUploadPreviewResult | null>(null);
  /** 提交失败后：在弹窗内提示，并已尝试用最新服务端结果刷新下方统计 */
  const [submitModalAlert, setSubmitModalAlert] = useState<{ type: 'warning' | 'error'; message: string } | null>(null);
  const [pendingUpload, setPendingUpload] = useState<{
    file: File;
    fileName: string;
    fileBase64: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** 弹窗标题：选文件预校验后为「预校验结果」，点击确认上传后为「上传结果」 */
  const [previewModalPhase, setPreviewModalPhase] = useState<'preview' | 'uploadResult'>('preview');
  const [uploadUiKey, setUploadUiKey] = useState(0);
  const actionLockRef = useRef(false);
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTaskKey, setDrawerTaskKey] = useState<string | null>(null);
  const [filters, setFilters] = useState<UploadTaskFilters>(() => createDefaultFilters());
  const [appliedFilters, setAppliedFilters] = useState<UploadTaskFilters>(() => createDefaultFilters());
  const [selectedTaskKeys, setSelectedTaskKeys] = useState<string[]>([]);
  const [downloadSubmitting, setDownloadSubmitting] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadModalFileName, setDownloadModalFileName] = useState('');

  const filterQueryVars = useMemo(() => toQueryVariables(appliedFilters), [appliedFilters]);
  const tasksPageVariables = useMemo(
    () => ({
      ...filterQueryVars,
      page: tablePage,
      size: tablePageSize,
    }),
    [filterQueryVars, tablePage, tablePageSize]
  );
  const uploaderOptionsVariables = useMemo(
    () => ({
      fileName: appliedFilters.fileName.trim() || null,
      uploadTime: filterQueryVars.uploadTime,
    }),
    [appliedFilters.fileName, filterQueryVars.uploadTime]
  );
  const { permission, tasksPageQuery, uploaderOptionsQuery } = useUndeliverableUploadQueries(
    tasksPageVariables,
    uploaderOptionsVariables
  );
  const { previewByFileMutation, confirmByFileMutation, submitDetailFilesToDownloadCenterMutation } =
    useUndeliverableUploadMutations();
  const filterContainerClassName = useFilterContainerClassName();
  const filterInputContainerClassName = useFilterInputContainerClassName();

  const canUpload = permission.isEnabled === true;
  const canUploadReady = canUpload && !permission.loading;

  const tasksPagePayload = tasksPageQuery.data?.listUndeliverableUploadTasksPage;
  const uploadTasks: UploadTaskRow[] = useMemo(() => {
    const raw = tasksPagePayload?.content;
    const content = (Array.isArray(raw) ? raw : []) as UndeliverableTaskSummaryFromApi[];
    return content.map((item: UndeliverableTaskSummaryFromApi) => ({
      key: item.legacyGroup
        ? `legacy:${item.fileObjectKey}:${item.createdBy ?? ''}`
        : `task:${item.taskNo}`,
      taskNo: item.taskNo,
      fileObjectKey: item.fileObjectKey,
      createdBy: item.createdBy,
      legacyGroup: item.legacyGroup,
      displayFileName: displayFileNameFromTask(item.originalFileName, item.fileObjectKey),
      uploadTime: item.uploadTime ?? null,
      uploaderDisplay: item.uploaderName || item.createdBy || '-',
    }));
  }, [tasksPagePayload]);

  const uploaderOptions = useMemo(() => {
    const list = uploaderOptionsQuery.data?.listUndeliverableUploadUploaderOptions ?? [];
    return list.map((o: { accountId: number | string; displayName: string }) => ({
      label: o.displayName,
      value: Number(o.accountId),
    }));
  }, [uploaderOptionsQuery.data]);

  const drawerTask = useMemo(() => uploadTasks.find((row) => row.key === drawerTaskKey) || null, [drawerTaskKey, uploadTasks]);

  const tasksTotal = Number(tasksPagePayload?.totalElements ?? 0);

  const modalOkText = useMemo(() => {
    const invalidCount = Number(previewResult?.invalidCount ?? 0);
    if (!Number.isFinite(invalidCount) || invalidCount > 0) {
      return t('qualityControl.undeliverableUpload.modal.okUploadValidOnly');
    }
    return t('qualityControl.undeliverableUpload.modal.okUploadAll');
  }, [previewResult?.invalidCount, t]);
  const hasPreviewErrors = (previewResult?.errors || []).length > 0;

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(tasksTotal / tablePageSize) || 1);
    if (tablePage > maxPage) {
      setTablePage(maxPage);
    }
  }, [tasksTotal, tablePage, tablePageSize]);

  const handleFilter = () => {
    setAppliedFilters({ ...filters });
    setTablePage(1);
    setSelectedTaskKeys([]);
  };

  const handleResetFilters = () => {
    const next = createDefaultFilters();
    setFilters(next);
    setAppliedFilters(next);
    setTablePage(1);
    setSelectedTaskKeys([]);
  };

  /** 勾选任务 → 后端按任务各生成一个明细 xlsx 登记到下载中心，用户到质检下载页取 */
  const handleDownloadFiles = async () => {
    if (selectedTaskKeys.length === 0) {
      message.warning(t('qualityControl.undeliverableUpload.messages.downloadNoSelection'));
      return;
    }
    setDownloadSubmitting(true);
    try {
      const tasks: UndeliverableUploadTaskKeyInput[] = uploadTasks
        .filter((row) => selectedTaskKeys.includes(row.key))
        .map((row) =>
          row.legacyGroup
            ? { legacyFileObjectKey: row.fileObjectKey, legacyCreatedBy: row.createdBy ?? null }
            : { taskNo: row.taskNo }
        );
      const result = await submitDetailFilesToDownloadCenterMutation({
        variables: { tasks, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
      });
      const fileNames = result.data?.submitUndeliverableUploadDetailFilesToDownloadCenter ?? [];
      message.success(
        t('qualityControl.undeliverableUpload.messages.downloadSubmitted', { count: fileNames.length })
      );
      setDownloadModalFileName(fileNames.join('、'));
      setDownloadModalOpen(true);
      setSelectedTaskKeys([]);
    } catch (e) {
      // 失败原因（如超过条数上限）由 errorLink 的全局 toast 按 messageKey 展示，此处不重复提示
      console.error(e);
    } finally {
      setDownloadSubmitting(false);
    }
  };

  const runPreviewFromFile = async (file: File) => {
    if (actionLockRef.current || !canUploadReady) return;
    actionLockRef.current = true;
    setSubmitting(true);
    setSubmitModalAlert(null);
    try {
      const fileBase64 = await readFileAsBase64(file, t('qualityControl.undeliverableUpload.messages.fileReadFailed'));
      const result = await previewByFileMutation({
        variables: {
          fileBase64,
          fileName: file.name,
        },
      });
      const normalized = normalizePreviewFromGraphql(result.data?.previewUndeliverableUploadByFile);
      setPreviewResult(normalized);
      setPendingUpload({ file, fileName: file.name, fileBase64 });
      setPreviewModalPhase('preview');
      setPreviewOpen(true);
    } catch (e: any) {
      message.error(e.message || t('qualityControl.undeliverableUpload.messages.previewFailed'));
    } finally {
      setSubmitting(false);
      actionLockRef.current = false;
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(UNDELIVERABLE_TEMPLATE_PUBLIC_PATH);
      if (!response.ok) {
        message.error(t('qualityControl.undeliverableUpload.messages.templateNotFound'));
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = t('qualityControl.undeliverableUpload.templateFileName');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success(t('qualityControl.undeliverableUpload.messages.templateDownloadSucceeded'));
    } catch (e) {
      console.error(e);
      message.error(t('qualityControl.undeliverableUpload.messages.templateDownloadFailed'));
    }
  };

  const beforeUpload = (file: File) => {
    if (!canUploadReady) {
      return Upload.LIST_IGNORE;
    }
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      message.error(t('qualityControl.undeliverableUpload.messages.onlyXlsx'));
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error(t('qualityControl.undeliverableUpload.messages.maxFileSize5mb'));
      return Upload.LIST_IGNORE;
    }
    void runPreviewFromFile(file);
    return false;
  };

  const handleConfirmUpload = async () => {
    if (actionLockRef.current) return;
    if (!pendingUpload) return;
    actionLockRef.current = true;
    setPreviewModalPhase('uploadResult');
    setSubmitting(true);
    setSubmitModalAlert(null);
    let fileBase64ForRefresh = pendingUpload.fileBase64;
    try {
      const fileBase64 =
        fileBase64ForRefresh ||
        (await readFileAsBase64(pendingUpload.file, t('qualityControl.undeliverableUpload.messages.fileReadFailed')));
      fileBase64ForRefresh = fileBase64;
      setPendingUpload((prev) => (prev ? { ...prev, fileBase64 } : prev));
      const expectedValidBeforeConfirm = previewResult?.validCount ?? 0;
      const expectedFingerprint = previewResult?.validPayloadFingerprint ?? '';
      const result = await confirmByFileMutation({
        variables: {
          fileBase64,
          fileName: pendingUpload.fileName,
          clientExpectedValidCount: expectedValidBeforeConfirm,
          clientExpectedValidPayloadFingerprint: expectedFingerprint,
        },
      });
      const insertedCount = Number(result.data?.confirmUndeliverableUploadByFile?.insertedCount ?? 0);
      message.success(t('qualityControl.undeliverableUpload.messages.confirmSucceeded', { count: insertedCount }));
      setPreviewOpen(false);
      setPreviewResult(null);
      setSubmitModalAlert(null);
      setPendingUpload(null);
      setUploadUiKey((k) => k + 1);
      await tasksPageQuery.refetch();
    } catch (e: unknown) {
      console.error(e);
      // errorLink 会对 GraphQL 错误弹全局 toast；本页在弹窗内用 Alert + 刷新预览承接，故关闭刚弹出的 message，避免重复与一闪而过
      message.destroy();
      const confirmErrMsg = userMessageFromUndeliverableConfirmGraphqlError(
        e,
        t,
        'qualityControl.undeliverableUpload.messages.confirmFailed'
      );
      const b64 = fileBase64ForRefresh;
      if (!b64) {
        setSubmitModalAlert({
          type: 'error',
          message: t('qualityControl.undeliverableUpload.modal.submitFailedPreviewUnavailable', {
            detail: confirmErrMsg,
          }),
        });
      } else {
        try {
          const refresh = await previewByFileMutation({
            variables: {
              fileBase64: b64,
              fileName: pendingUpload.fileName,
            },
          });
          const next = normalizePreviewFromGraphql(refresh.data?.previewUndeliverableUploadByFile);
          if (next) {
            setPreviewResult(next);
            setSubmitModalAlert({
              type: 'warning',
              message: t('qualityControl.undeliverableUpload.modal.submitStaleRefreshed', { detail: confirmErrMsg }),
            });
          } else {
            setSubmitModalAlert({
              type: 'error',
              message: t('qualityControl.undeliverableUpload.modal.submitFailedNoPreview', { detail: confirmErrMsg }),
            });
          }
        } catch {
          setSubmitModalAlert({
            type: 'error',
            message: t('qualityControl.undeliverableUpload.modal.submitFailedPreviewUnavailable', {
              detail: confirmErrMsg,
            }),
          });
        }
      }
    } finally {
      setSubmitting(false);
      actionLockRef.current = false;
    }
  };

  const openDrawerForTask = useCallback((task: UploadTaskRow) => {
    setDrawerTaskKey(task.key);
    setDrawerOpen(true);
  }, []);

  const fileNameFilterLabel = t('qualityControl.undeliverableUpload.filters.fileName');
  const uploaderFilterLabel = t('qualityControl.undeliverableUpload.filters.uploader');
  const uploadTimeFilterLabel = t('qualityControl.undeliverableUpload.filters.uploadTime');

  const taskColumns: ColumnsType<UploadTaskRow> = useMemo(
    () => [
      {
        title: t('qualityControl.undeliverableUpload.table.taskNo'),
        dataIndex: 'taskNo',
        key: 'taskNo',
        align: 'left',
        width: 280,
        fixed: 'left',
        render: (v: string, row: UploadTaskRow) => (
          <Button
            type="link"
            className="h-auto p-0 font-inherit whitespace-nowrap"
            style={{ color: '#722ed1' }}
            onClick={() => openDrawerForTask(row)}
          >
            {v || '-'}
          </Button>
        ),
      },
      {
        title: t('qualityControl.undeliverableUpload.table.fileName'),
        dataIndex: 'displayFileName',
        key: 'displayFileName',
        align: 'left',
        width: 220,
        render: (v: string) => (
          <span className="text-black" style={{ whiteSpace: 'nowrap' }}>
            {v || '-'}
          </span>
        ),
      },
      {
        title: t('qualityControl.undeliverableUpload.table.uploadTime'),
        dataIndex: 'uploadTime',
        key: 'uploadTime',
        align: 'left',
        width: 180,
        render: (v: string | null) => (
          <span style={{ whiteSpace: 'nowrap' }}>{v ? formatISODateTime(v) : '-'}</span>
        ),
      },
      {
        title: t('qualityControl.undeliverableUpload.table.uploader'),
        dataIndex: 'uploaderDisplay',
        key: 'uploaderDisplay',
        align: 'left',
        width: 180,
        render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
      },
      {
        title: t('qualityControl.undeliverableUpload.table.progress'),
        key: 'progress',
        align: 'left',
        width: 100,
        render: () => (
          <span className="text-black font-semibold" style={{ whiteSpace: 'nowrap' }}>
            {t('qualityControl.undeliverableUpload.table.success')}
          </span>
        ),
      },
    ],
    [openDrawerForTask, t]
  );

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <h2 className="mb-4 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xl font-semibold text-black">
        <span>{t('qualityControl.undeliverableUpload.title')}</span>
        <span className="text-base font-normal text-black">
          {t('qualityControl.undeliverableUpload.subtitle')}
        </span>
      </h2>
      <div className={`${INSTRUCTION_PURPLE_BOX_CLASS} space-y-2`}>
        <p>
          {t('qualityControl.undeliverableUpload.instructions.line1Prefix')}
          <Button
            type="link"
            className="h-auto p-0 align-baseline font-inherit underline"
            onClick={() => void handleDownloadTemplate()}
          >
            {t('qualityControl.undeliverableUpload.instructions.line1Link')}
          </Button>
          {t('qualityControl.undeliverableUpload.instructions.line1Suffix')}
        </p>
        <p>
          {t('qualityControl.undeliverableUpload.instructions.line2')}
        </p>
      </div>

      <div className="mb-2 py-2">
        <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
          <div className="min-w-0">
            <InputFilterItem
              label={fileNameFilterLabel}
              value={filters.fileName}
              onChange={(e) => setFilters((prev) => ({ ...prev, fileName: e.target.value }))}
              spanClassName={getSmartLabelClassName([fileNameFilterLabel])}
              placeholder={String(t('qualityControl.undeliverableUpload.filters.fileNamePlaceholder'))}
            />
          </div>
          <div className="min-w-0">
            <div className={filterContainerClassName}>
              <span className={getSmartLabelClassName([uploaderFilterLabel])}>{uploaderFilterLabel}</span>
              <div className={filterInputContainerClassName}>
                <Select
                  allowClear
                  showSearch
                  className="w-full"
                  size="middle"
                  value={filters.uploaderAccountId}
                  placeholder={t('qualityControl.undeliverableUpload.filters.allUploaders')}
                  options={uploaderOptions}
                  filterOption={(inputValue, option) =>
                    String(option?.label ?? '').toLowerCase().includes(inputValue.toLowerCase())
                  }
                  onChange={(value) => setFilters((prev) => ({ ...prev, uploaderAccountId: value ?? null }))}
                />
              </div>
            </div>
          </div>
          <div className="min-w-0">
            <DayjsRangePickerFilterItem
              label={uploadTimeFilterLabel}
              value={filters.uploadTimeRange}
              onChange={(range) => setFilters((prev) => ({ ...prev, uploadTimeRange: range }))}
              spanClassName={getSmartLabelClassName([uploadTimeFilterLabel])}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end items-center gap-2 mt-4 mb-4">
        <Button
          type="primary"
          onClick={handleFilter}
          className="w-auto min-w-24"
        >
          <span className="font-bold">{t('qualityControl.undeliverableUpload.buttons.filter')}</span>
        </Button>
        <Button
          onClick={handleResetFilters}
          className="w-auto min-w-24"
        >
          <span className="font-bold">{t('qualityControl.undeliverableUpload.buttons.reset')}</span>
        </Button>
        <Tooltip title={!canUpload ? t('qualityControl.undeliverableUpload.messages.uploadDisabledByWhitelist') : undefined}>
          <span className="inline-flex">
            <Upload
              key={uploadUiKey}
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              beforeUpload={beforeUpload}
              disabled={!canUploadReady}
              showUploadList={false}
              maxCount={1}
            >
              <Button
                type="primary"
                className="w-auto min-w-24"
                disabled={!canUploadReady}
                loading={submitting || permission.loading}
              >
                <span className="font-bold">{t('qualityControl.undeliverableUpload.buttons.upload')}</span>
              </Button>
            </Upload>
          </span>
        </Tooltip>
        <Button
          type="primary"
          onClick={() => void handleDownloadFiles()}
          disabled={selectedTaskKeys.length === 0}
          loading={downloadSubmitting}
          className="w-auto min-w-24"
        >
          <span className="font-bold">{t('qualityControl.undeliverableUpload.buttons.downloadFiles')}</span>
        </Button>
      </div>

      <Table<UploadTaskRow>
        rowKey="key"
        className="mb-4"
        columns={taskColumns}
        dataSource={uploadTasks}
        loading={tasksPageQuery.loading}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        pagination={false}
        rowSelection={{
          selectedRowKeys: selectedTaskKeys,
          onChange: (keys) => setSelectedTaskKeys(keys as string[]),
        }}
      />
      <TablePaginationBar
        total={tasksTotal}
        current={tablePage}
        pageSize={tablePageSize}
        onChange={(page, pageSize) => {
          setTablePage(page);
          setTablePageSize(pageSize);
          setSelectedTaskKeys([]);
        }}
      />

      <UndeliverableUploadTaskDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        task={drawerTask}
      />

      <AsyncReportDownloadModal
        visible={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        fileName={downloadModalFileName}
        reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.undeliverableUploadDetail}
      />

      <Modal
        title={t(
          previewModalPhase === 'preview'
            ? 'qualityControl.undeliverableUpload.modal.titlePreview'
            : 'qualityControl.undeliverableUpload.modal.titleUploadResult'
        )}
        open={previewOpen}
        width={hasPreviewErrors || submitModalAlert ? '55%' : 520}
        style={{ maxWidth: '92vw' }}
        onCancel={() => {
          setPreviewOpen(false);
          setSubmitModalAlert(null);
          setPreviewModalPhase('preview');
        }}
        onOk={handleConfirmUpload}
        confirmLoading={submitting}
        okButtonProps={{
          disabled:
            !previewResult ||
            previewResult.validCount === 0 ||
            !previewResult.validPayloadFingerprint,
          className: 'w-auto min-w-24 font-bold',
        }}
        cancelButtonProps={{ className: 'w-auto min-w-24 font-bold' }}
        okText={<span className="font-bold">{modalOkText}</span>}
        cancelText={<span className="font-bold">{t('components.button.cancel')}</span>}
      >
        {submitModalAlert && (
          <Alert
            className="mb-3"
            type={submitModalAlert.type}
            showIcon
            message={<span className="whitespace-pre-line">{submitModalAlert.message}</span>}
          />
        )}
        <p className="text-black">{t('qualityControl.undeliverableUpload.modal.totalCount', { count: previewResult?.totalCount ?? 0 })}</p>
        <p className="text-black">{t('qualityControl.undeliverableUpload.modal.validCount', { count: previewResult?.validCount ?? 0 })}</p>
        <p className={Number(previewResult?.invalidCount ?? 0) > 0 ? 'text-red-600 font-semibold' : 'text-black'}>
          {t('qualityControl.undeliverableUpload.modal.invalidCount', { count: previewResult?.invalidCount ?? 0 })}
        </p>
        {hasPreviewErrors && (
          <p className="text-sm text-black">{t('qualityControl.undeliverableUpload.modal.errorHint')}</p>
        )}
        {hasPreviewErrors && (
          <div className="mt-3 max-h-60 overflow-auto rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {(previewResult?.errors ?? []).map((item) => (
              <div key={`${item.rowNo}-${item.message}`}>
                {t('qualityControl.undeliverableUpload.modal.errorLine', { rowNo: item.rowNo, message: item.message })}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UndeliverableUploadPage;
